/**
 * RequireCapability - route guard that gates a route on a runtime feature
 * flag. When the flag resolves false (or is still loading and we choose to
 * treat undefined as off), the guarded element redirects to `redirectTo`.
 *
 * Used to keep the agile routes registered in App.tsx (so the nav-coverage
 * gates still map each route) while making them inert when the capability is
 * turned off in Configuration > Capabilities.
 *
 * Part of the Hybrid Waterfall/Agile Delivery feature (P0 Foundation).
 */

import { Navigate } from 'react-router-dom'
import { useFlag } from '@/hooks/useFlag'

export function RequireCapability({
  flag,
  redirectTo,
  children,
}: {
  flag: string
  redirectTo: string
  children: React.ReactNode
}) {
  const enabled = useFlag<boolean>(flag)

  // `undefined` means the flag query is still resolving. Render nothing rather
  // than flashing the page or redirecting prematurely.
  if (enabled === undefined) return null
  if (!enabled) return <Navigate to={redirectTo} replace />
  return <>{children}</>
}
